import { formatCurrencyAmount } from '@/lib/currency'

import type { BudgetVarianceAgentSplit } from '@scrambl/shared'
import { sumKnownMoney } from '@scrambl/shared'

export type BudgetMode = 'budget' | 'forecast'

export type BudgetHealth = 'green' | 'amber' | 'red' | null

export interface BudgetVarianceRow {
  id: string
  name: string
  groupId?: string | null
  groupName?: string | null
  actualRevenue: number | null
  budgetRevenue: number | null
  forecastRevenue: number | null
  actualCost: number | null
  /** Activity in the selected period, independent of whether it is priced. */
  hasPeriodActivity?: boolean
  /** Lifetime actuals through the period end — what a budget burns against. */
  toDateRevenue?: number | null
  /** Lifetime actuals on the budget's own side; what burn compares. */
  budgetActual?: number | null
  toDateCost?: number | null
  /** Explicit unit of the budget, forecast and budgetActual. Absent on older APIs. */
  budgetBasis?: 'FEE' | 'COST' | 'HOURS' | null
  budgetStatus?: 'DRAFT' | 'ACTIVE' | 'CLOSED' | null
  /**
   * Agent work inside this row's period actuals (agent labor ledger; only
   * when the org has it and the split was requested).
   */
  agent?: BudgetVarianceAgentSplit
}

/** Missing legacy properties may fall back; an explicit unknown amount may not. */
export function budgetActualForRow(row: BudgetVarianceRow): number | null {
  if (row.budgetActual !== undefined) return row.budgetActual
  if (row.toDateRevenue !== undefined) return row.toDateRevenue
  return row.actualRevenue
}

/** A comparison needs an explicit unit; older API responses stay unknown. */
export function budgetComparisonActual(row: BudgetVarianceRow): number | null {
  if (!row.budgetBasis) return null
  return row.budgetBasis === 'FEE'
    ? budgetActualForRow(row)
    : (row.budgetActual ?? null)
}

export function budgetComparisonTotals(rows: BudgetVarianceRow[]) {
  const firstBasis = rows[0]?.budgetBasis ?? null
  const basis =
    firstBasis && rows.every(row => row.budgetBasis === firstBasis)
      ? firstBasis
      : null
  return {
    basis,
    budget: basis ? sumKnownMoney(rows.map(row => row.budgetRevenue)) : null,
    forecast: basis
      ? sumKnownMoney(rows.map(row => row.forecastRevenue))
      : null,
    actual: basis ? sumKnownMoney(rows.map(budgetComparisonActual)) : null,
  }
}

export interface BurnChartPoint {
  weekStart: string
  cumulativeActualSpend?: number | null
  plannedSpend?: number | null
  confirmedPlannedSpend?: number | null
  tentativePlannedSpend?: number | null
}

export interface MarginTrendPoint {
  period: string
  revenue?: number | null
  cost?: number | null
  marginPercent: number | null
}

export interface BudgetCoverage {
  withBudget: number
  total: number
}

/** Which record a shared budget component reads: a project or an entity. */
export type BreakdownScope =
  | { level: 'project'; projectId: string }
  | { level: 'entity'; entityId: string }

/** Burn-section scope; entity budgets can pin a specific period via budgetId. */
export type BurnChartScope =
  | { level: 'project'; projectId: string }
  | { level: 'entity'; entityId: string; budgetId?: string }

export function formatMoney(
  n: number | null | undefined,
  currency = 'USD',
): string {
  return formatCurrencyAmount(n, currency)
}
